import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  StepUpBeginResponse,
  StepUpCeremonyService,
  StepUpChallenge,
  StepUpCompleteResponse,
  StepUpCompletionLane,
} from '@toybank/ui';
import { Observable, filter, finalize, map, switchMap, take, tap } from 'rxjs';
import { MockPhoneService } from './phone.service';
import { CUSTOMER_PORTAL_BRAND } from '../brand';

interface DebugBeginResponse {
  debugChallengeId: string;
  deviceCode: string;
  expiresAt: string;
  from: string;
  note: string;
}

interface DebugApprovalResponse {
  approved: boolean;
}

interface DebugCompleteResponse {
  stepUpNonce: string;
}

/**
 * Debug-build adapter for the ordinary step-up dialog.
 *
 * The username shortcut never satisfies this service. Begin asks the BFF for a server-bound
 * fake-device challenge, displays its intercepted code in the wrapper phone, and waits for an
 * explicit tap before asking the server to approve it. Complete returns only the opaque,
 * operation-bound nonce; no token, ACR, or reusable factor state reaches the browser.
 */
@Injectable()
export class DebugStepUpService {
  private readonly http = inject(HttpClient);
  private readonly phone = inject(MockPhoneService);
  private readonly ceremony = inject(StepUpCeremonyService);
  private readonly activeChallenges = new Map<string, string>();

  /**
   * This device completes out of band: the code is delivered to the wrapper phone and the user
   * taps it there. No popup is involved, and the dialog must not open one — a blocked popup
   * would otherwise abort the ceremony before `begin()` ever delivered a code to the phone.
   */
  readonly completionLane: StepUpCompletionLane = 'OUT_OF_BAND';

  begin(challenge: StepUpChallenge): Observable<StepUpBeginResponse> {
    // The pending ceremony's own actionLabel ("Approve payment order ORDER-…", "Pay $500.00 USD
    // to Acme", …) is the best available description of WHICH operation this device push belongs
    // to. It is read here rather than threaded through begin()'s signature so the shared
    // StepUpService/DebugStepUpService contract stays untouched — only this debug adapter needs
    // it, to label the phone message (see MockPhoneService.autofill's doc on why an unlabeled
    // phone message is ambiguous once a second ceremony is in flight).
    const operation = this.ceremony.pendingCeremony()?.context.actionLabel;
    return this.http.post<DebugBeginResponse>('/bff/debug/step-up/begin', {
      challengeId: challenge.challengeId,
    }).pipe(
      tap((response) => {
        // The BFF labels the sender with the internal project name ("ToyBank debug device"); the
        // phone is a customer-facing surface, so it always shows the portal brand.
        this.phone.deliver(
          CUSTOMER_PORTAL_BRAND,
          response.deviceCode,
          response.note,
          true,
          { kind: 'APPROVE', operation },
        );
      }),
      switchMap((response) => this.phone.tappedCodes.pipe(
        filter((code) => code === response.deviceCode),
        take(1),
        switchMap((deviceCode) => this.http.post<DebugApprovalResponse>(
          '/bff/debug/step-up/approve',
          { debugChallengeId: response.debugChallengeId, deviceCode },
        )),
        filter((approval) => approval.approved === true),
        tap(() => this.activeChallenges.set(challenge.challengeId, response.debugChallengeId)),
        map(() => ({
          completionMode: 'OUT_OF_BAND' as const,
          redirectUri: '',
          callbackChannel: '',
          expiresInSeconds: 1,
        })),
      )),
    );
  }

  complete(challenge: StepUpChallenge): Observable<StepUpCompleteResponse> {
    const debugChallengeId = this.activeChallenges.get(challenge.challengeId);
    if (!debugChallengeId) {
      throw new Error('No approved debug-device challenge exists for this operation.');
    }
    return this.http.post<DebugCompleteResponse>('/bff/debug/step-up/complete', {
      debugChallengeId,
    }).pipe(
      map((response) => ({ satisfied: true, stepUpNonce: response.stepUpNonce })),
      finalize(() => this.activeChallenges.delete(challenge.challengeId)),
    );
  }
}
